import { useRef } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getReviewsForProperty } from "@/lib/reviews";

export default function ReviewsCarousel({ propertyId, propertyRating = 4.85, propertyReviewCount = 50 }) {
  const reviews = getReviewsForProperty(propertyId, 6);
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-review-card]");
    const w = (card?.offsetWidth || 380) + 16;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  return (
    <section data-testid="reviews-carousel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="kicker mb-2">Guest reviews</div>
          <h2 className="font-sora text-2xl md:text-3xl font-semibold tracking-tight text-[#0E2E24] leading-tight">
            What guests are saying
          </h2>
          <div className="mt-3 flex items-center gap-3 text-sm font-sora text-[#5B6B63]">
            <span className="flex items-center gap-1.5 text-[#0E2E24]">
              <Star size={15} className="fill-[#F4A930] text-[#F4A930]" />
              <span className="font-semibold">{propertyRating.toFixed(2)}</span>
              <span className="text-[#5B6B63] font-normal">· {propertyReviewCount} reviews</span>
            </span>
            <span className="text-[#0E2E24]/20">|</span>
            <span>98% guests recommend this stay</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => scroll(-1)} className="h-10 w-10 rounded-full border-[#0E2E24]/15 text-[#0E2E24] hover:bg-[#E8F2EC]" data-testid="reviews-prev">
            <ChevronLeft size={18} />
          </Button>
          <Button variant="outline" size="icon" onClick={() => scroll(1)} className="h-10 w-10 rounded-full border-[#0E2E24]/15 text-[#0E2E24] hover:bg-[#E8F2EC]" data-testid="reviews-next">
            <ChevronRight size={18} />
          </Button>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-1 px-1"
      >
        {reviews.map((r) => (
          <figure
            key={r.id}
            data-review-card
            className="snap-start shrink-0 w-[320px] md:w-[380px] bg-white border border-[#0E2E24]/10 rounded-2xl p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-[#F4A930]">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} className={i < Math.round(r.rating) ? "fill-[#F4A930] text-[#F4A930]" : "text-[#0E2E24]/15"} />
                ))}
              </div>
              <Quote size={20} className="text-[#7CB342]/60" />
            </div>
            <blockquote className="mt-4 text-[15px] leading-relaxed text-[#0E2E24] line-clamp-5">
              "{r.quote}"
            </blockquote>
            <figcaption className="mt-5 pt-5 border-t border-[#0E2E24]/10 flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[#E8F2EC] overflow-hidden">
                <img src={r.avatar} alt={r.name} className="img-cover" />
              </div>
              <div>
                <div className="font-sora text-sm font-semibold text-[#0E2E24]">{r.name}</div>
                <div className="text-xs text-[#5B6B63] font-sora">{r.occasion} · {r.date}</div>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}
